import {
  createElement,
  CSSProperties,
  ReactNode,
  Ref,
  MouseEventHandler,
  KeyboardEventHandler,
  useState,
  useRef,
  useEffect,
  Fragment,
  ReactElement,
} from 'react';

import { postfixTestId, TestIdProps } from '../../util/testId';
import { Button, ButtonVariant } from '../Button';
import { Link } from '../Link';
import { Icon, IconProps } from '../Icon';
import { Text } from '../Text';
import { renderInlineCssVars, useDesignTokens, DesignTokens } from '../../theme';
import { useRefArray } from '../../hooks/useRefArray';

import { ArrowCollapsed } from '@sas/react-svg';
import { useDragAndDropComponents } from '../../dnd/DndComponentsContext';
import {
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  DragUpdate,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
} from '../../dnd';
import { InvisibleText } from '../InvisibleText';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { ToolbarItem } from '../Toolbar';
import bundle from './NovaComponents-WindowShade-gui-icu.properties';

import {
  BaseWindowShadeProps,
  BaseWindowShadeOpenSectionProps,
  WindowShadeHeaderAction,
  WindowShadeVariant,
} from './types';

import cx from 'classnames';
import cn from './WindowShade.css.json';
import './WindowShade.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const BaseWindowShade = (props: BaseWindowShadeProps & BaseWindowShadeOpenSectionProps) => {
  const { refs: headerRefs, createSetRef: createHeaderRef } = useRefArray<HTMLDivElement>();
  const droppableContainerRef = useRef<HTMLDivElement | null>(null);
  const DNDComponents = useDragAndDropComponents();

  const dndIdPrefix = props.dndIdPrefix ?? 'windowshade';

  const dt = useDesignTokens();

  const [selfItemDragged, setSelfItemDragged] = useState(true);

  const getOnSectionKeyDown = (index: number): KeyboardEventHandler => (event) => {
    if (headerRefs.includes(event.target as HTMLDivElement)) {
      if (event.key === 'Home') {
        headerRefs[0]?.focus();
        event.preventDefault();
      } else if (event.key === 'End') {
        headerRefs[headerRefs?.length - 1]?.focus();
        event.preventDefault();
      } else if (event.key === 'ArrowDown') {
        const index = headerRefs.indexOf(event.target as HTMLDivElement);
        // NOTE: bounds check doesn't matter here
        headerRefs[index + 1]?.focus();
        event.preventDefault();
      } else if (event.key === 'ArrowUp') {
        const index = headerRefs.indexOf(event.target as HTMLDivElement);
        // NOTE: bounds check doesn't matter here
        headerRefs[index - 1]?.focus();
        event.preventDefault();
      }
    }
    if (event.key === 'Enter' || event.key === ' ') {
      props.onToggleSection(index);
      event.preventDefault();
    }
  };

  const getOnSectionHeaderSelect = (index: number) => () => props.onToggleSection(index);

  const areSectionsToBeReordered = (result: DropResult) => {
    return result.destination &&
      result.reason === 'DROP' &&
      `${dndIdPrefix}-droppable` === result.destination.droppableId &&
      result.destination.droppableId === result.source.droppableId &&
      result.destination.index !== result.source.index
      ? true
      : false;
  };

  const separatorId = `${dndIdPrefix}-dragOver-separator`;

  const addDragOverSeparator = (parentElement: HTMLElement, className?: string) => {
    if (parentElement) {
      const separator = document.createElement('div');
      separator.className = className ?? 'dndIndicator';
      separator.id = separatorId;
      parentElement.appendChild(separator);
    }
  };

  const removeDragOverSeperator = () => {
    document.getElementById(separatorId)?.remove();
  };

  // render functions //
  function renderDraggableWindowShadeSection(
    index: number,
    dragId: string,
    sectionProps: BaseWindowShadeSectionProps,
    content: ReactNode
  ) {
    const { Draggable } = DNDComponents!;
    return (
      <Draggable
        draggableId={dragId}
        key={index}
        index={index}
        isDragDisabled={!sectionProps.isDraggable}
      >
        {(provided) => renderWindowShadeSection(index, { ...sectionProps }, content, provided)}
      </Draggable>
    );
  }

  function renderWindowShadeSection(
    index: number,
    sectionProps: BaseWindowShadeSectionProps,
    content: ReactNode,
    provided?: DraggableProvided
  ) {
    return (
      <WindowShadeSection {...(!provided && { key: index })} {...sectionProps} provided={provided}>
        {content}
      </WindowShadeSection>
    );
  }

  function renderSectionsContainer(
    provided?: DroppableProvided,
    snapshot?: DroppableStateSnapshot
  ) {
    const spacingTokens = {
      ...renderInlineCssVars(getSpacingTokens(dt), 'windowshade'),
    };

    return (
      <div
        ref={(element) => {
          provided?.innerRef(element);

          droppableContainerRef.current = element;
        }}
        className={cx(cn.container, props.className)}
        style={{
          ...spacingTokens,
          ...props.style,
          ...(provided && props.sections.length === 0 && { minHeight: '1rem' }),
        }}
        data-testid={props.testId}
      >
        {props.sections.map(
          (
            {
              header,
              disabled,
              content,
              maxHeight,
              headerAction,
              headingClassName,
              headingStyle,
              allowDrag,
              required,
              headingIcon,
              renderToolbar,
            },
            index
          ) => {
            const dragId = `${dndIdPrefix}-draggable-${index}`;
            const isDragClone = snapshot?.draggingFromThisWith === dragId;

            const sectionProps: BaseWindowShadeSectionProps = {
              wrapHeaderText: props.wrapHeaderText,
              headerRef: createHeaderRef(index),
              header,
              headerAction,
              headingClassName,
              headingStyle,
              disabled,
              maxHeight,
              isOpen: props.openSections.includes(index),
              onKeyDown: getOnSectionKeyDown(index),
              onSelectHeader: getOnSectionHeaderSelect(index),
              testId: postfixTestId(props.testId, `section-${index}`),
              variant: props.variant,
              isDragClone,
              isDraggable: allowDrag ?? false,
              belongsToDraggedDestination: selfItemDragged,
              required,
              headingIcon,
              showHeadingIcon: props.showHeadingIcon,
              renderToolbar,
            };

            return provided && !isDragClone
              ? renderDraggableWindowShadeSection(index, dragId, sectionProps, content)
              : renderWindowShadeSection(index, sectionProps, content);
          }
        )}
        {snapshot?.isDraggingOver && <div style={{ height: '0.25rem' }} />}
      </div>
    );
  }

  function renderDragEnabledSectionsContainer() {
    const { DragDropContext, Droppable } = DNDComponents!;
    const allowDrop = props.allowDrop ?? false;
    return (
      <DragDropContext
        onDragUpdate={(result: DragUpdate) => {
          removeDragOverSeperator();

          if (result.destination && `${dndIdPrefix}-droppable` === result.destination.droppableId) {
            const totalDraggables = props.sections.length;
            const addAtBottom = result.destination.index === totalDraggables;

            if (
              result.destination.droppableId === result.source.droppableId &&
              (result.source.index === result.destination.index || addAtBottom)
            ) {
              return;
            }

            const dropIndexDescriptorId = `${dndIdPrefix}-draggable-${result.destination.index.toString()}`;

            const dropIndexDomRef: HTMLElement | null = addAtBottom
              ? droppableContainerRef.current
              : document.querySelector(`[data-rbd-draggable-id='${dropIndexDescriptorId}']`);

            if (dropIndexDomRef) {
              addDragOverSeparator(dropIndexDomRef, addAtBottom ? 'dndBottomIndicator' : undefined);
            }
          }
        }}
        onBeforeCapture={(result) => {
          const idParts = result.draggableId.split('-');
          if (idParts[0] !== dndIdPrefix) {
            setSelfItemDragged(false);
          }
        }}
        onDragEnd={(result: DropResult) => {
          setSelfItemDragged(true);
          removeDragOverSeperator();

          // this can only handle re-order
          if (areSectionsToBeReordered(result)) {
            props.onSectionsReorder?.(result.source.index, result.destination!.index);
          }
        }}
      >
        <Droppable
          isDropDisabled={!allowDrop}
          {...(props.allowedDropType && { type: props.allowedDropType })}
          droppableId={`${dndIdPrefix}-droppable`}
          renderClone={(
            provided: DraggableProvided,
            _: DraggableStateSnapshot,
            rubric: DraggableRubric
          ) => {
            const draggedSection = props.sections[rubric.source.index];
            const sectionProps = {
              wrapHeaderText: props.wrapHeaderText,
              header: draggedSection.header,
              headerAction: draggedSection.headerAction,
              headingClassName: draggedSection.headingClassName,
              headingStyle: draggedSection.headingStyle,
              disabled: draggedSection.disabled,
              maxHeight: draggedSection.maxHeight,
              isOpen: false,
              variant: props.variant,
              onSelectHeader: () => {},
              onKeyDown: () => {},
              headerRef: null,
              provided,
              isDragImage: true,
              required: draggedSection.required,
              headingIcon: draggedSection.headingIcon,
              showHeadingIcon: props.showHeadingIcon,
            };
            return <WindowShadeSection {...sectionProps} />;
          }}
        >
          {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) =>
            renderSectionsContainer(provided, snapshot)
          }
        </Droppable>
      </DragDropContext>
    );
  }
  return !!DNDComponents ? renderDragEnabledSectionsContainer() : renderSectionsContainer();
};

interface TransitionState {
  /**
   * Determines if we should show content.
   */
  isOpen: boolean;
  /** Props to put on the content section during a transition. */
  sectionTransitionProps?: {
    className: string;
    style: Pick<CSSProperties, 'transitionDuration' | 'height' | 'display'>;
  };
  /** Props to put on the icon during a transition. */
  iconTransitionProps?: {
    className: string;
    style: Pick<CSSProperties, 'transitionDuration'>;
  };
}

interface BaseWindowShadeSectionProps extends TestIdProps {
  children?: ReactNode;
  headingClassName?: string;
  headingStyle?: CSSProperties;
  header: string;
  isOpen: boolean;
  disabled: boolean | undefined;
  maxHeight: string | number | undefined;
  headerAction: Readonly<WindowShadeHeaderAction> | undefined;
  onSelectHeader: MouseEventHandler | undefined;
  onKeyDown: KeyboardEventHandler | undefined;
  headerRef: Ref<HTMLDivElement>;
  variant?: WindowShadeVariant;
  wrapHeaderText?: boolean;
  provided?: DraggableProvided;
  isDragImage?: boolean;
  isDragClone?: boolean;
  isDraggable?: boolean;
  belongsToDraggedDestination?: boolean;
  required?: boolean;
  headingIcon?: Pick<IconProps, 'icon' | 'color' | 'tooltip' | 'ariaLabel'>;
  showHeadingIcon?: boolean;
  renderToolbar?: () => ReactElement<ToolbarItem>;
}

const useHtmlId = createUseHtmlId('WindowShadeSection');

const WindowShadeSection = (props: BaseWindowShadeSectionProps) => {
  const contentSection = useRef<HTMLDivElement>(null);
  const localizer = useTextLocalizer();
  const windowShadeSectionId = useHtmlId();
  const isDraggable = props.provided && (props.isDraggable ?? true);
  const [
    { isOpen, sectionTransitionProps, iconTransitionProps },
    setTransition,
  ] = useState<TransitionState>({
    // NOTE: we initialize to props.isOpen to avoid transitioning on mount.
    isOpen: props.isOpen,
  });

  const getAnimationDuration = (height: number) => {
    /** rate = 16 pixels per 25ms */
    const rate = (1 / 16) * 25;

    return Math.max(Math.min(height * rate, 400), 100);
  };

  useEffect(() => {
    if (props.isOpen === isOpen || !contentSection.current) return;

    let sectionStyle: Required<TransitionState>['sectionTransitionProps']['style'];
    let iconStyle: Required<TransitionState>['iconTransitionProps']['style'];

    // NOTE: This animation code is close to the "FLIP" technique (except we don't use
    //     | transform, because this needs to affect layout during the transition).
    //     | ref: https://aerotwist.com/blog/flip-your-animations/
    if (props.isOpen) {
      // Transition from Closed => Open
      // measure the size of the content (we can't transition a height to/from 'auto')
      contentSection.current.style.height = 'auto';
      contentSection.current.style.display = '';
      // NOTE: reading clientHeight causes the browser to do layout (synchronously)
      const height = contentSection.current.clientHeight;
      // close the section back up, so we can start the transition on the next react render
      contentSection.current.style.height = '0px';
      // another synchronous layout for the browser. We use a function call instead of
      // reading a property to avoid issues with aggressive JS minifiers.
      contentSection.current.getBoundingClientRect();

      const transitionDuration = `calc(${getAnimationDuration(
        height
      )}ms * var(--nova-animation-duration-factor, 1))`;
      sectionStyle = { height, transitionDuration };
      iconStyle = { transitionDuration };
    } else {
      // Transition from Open => Closed
      const currentHeight = contentSection.current.clientHeight;
      // we can't transition from height="auto", so we set the size
      contentSection.current.style.height = `${currentHeight}px`;
      // This is a synchronous layout so the height mutation takes affect. We use a function call
      // instead of reading a property to avoid issues with aggressive JS minifiers.
      contentSection.current.getBoundingClientRect();

      const transitionDuration = `calc(${getAnimationDuration(
        currentHeight
      )}ms * var(--nova-animation-duration-factor, 1))`;
      sectionStyle = { height: 0, transitionDuration, display: undefined };
      iconStyle = { transitionDuration };
    }

    // NOTE: we use one setState call here to avoid multiple renders in Legacy React (before concurrent/batched).
    // NOTE: in concurrent react, this set call may need to explicitly have the highest priority possible
    //     | (higher priority updates that interrupt this render may cause transitions to snap).
    setTransition({
      isOpen: props.isOpen,
      sectionTransitionProps: { className: cn.contentTransitioning, style: sectionStyle },
      iconTransitionProps: { className: cn.headerIconTransitioning, style: iconStyle },
    });
  }, [isOpen, props.isOpen]);

  const dt = useDesignTokens();

  let tokens;

  if (props.disabled) {
    tokens = dt.enableNovaTokens
      ? dt.stateStandardContentNova.disabled
      : dt.stateStandardContent.disabled;
  } else {
    tokens = dt.enableNovaTokens
      ? dt.stateStandardContentNova.normal
      : dt.stateStandardContent.normal;
  }

  let focusTokens;
  if (dt.enableNovaTokens && dt.themeVariant === 'light' && props.variant === 'primary-light') {
    tokens = {
      ...tokens,
      fg: dt.c.colorTextInverse,
      'asterisk-fg': props.disabled ? dt.statePrimaryNova.disabled.fg : dt.c.colorAlert,
    };
    focusTokens = 'var(--focus-dark-bg)';
  } else {
    tokens = {
      ...tokens,
      'asterisk-fg': props.disabled ? dt.stateStandardNova.disabled.fg : dt.c.colorAlert,
    };
    focusTokens = 'var(--focus-light-bg)';
  }

  const headerTokenVars = {
    ...renderInlineCssVars(
      {
        ...tokens,
        'font-size-m': dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m,
      },
      'windowshade-header'
    ),
    ...renderInlineCssVars({ focus: focusTokens }, 'windowshade-header'),
  };

  const spacingTokens = {
    ...renderInlineCssVars(
      {
        ...getSpacingTokens(dt),
        'padding-start':
          props.showHeadingIcon && props.headingIcon
            ? 'var(--windowshade-spacing-s)'
            : 'var(--windowshade-spacing-m)',
      },
      'windowshade'
    ),
  };

  const setDraggableRefOnSectionContainer = !props.belongsToDraggedDestination || props.isDragClone;

  const requiredSymbol = '*';

  let headerAction;
  if (props.renderToolbar) {
    headerAction = props.renderToolbar();
  } else if (props.headerAction?.type === 'button') {
    headerAction = (
      <Button
        testId={postfixTestId(props.testId, 'action-button')}
        variant={ButtonVariant.Icon}
        disabled={props.headerAction.disabled ?? props.disabled}
        onPress={props.headerAction.onPress}
        icon={{ icon: props.headerAction.iconType }}
      />
    );
  } else if (props.headerAction?.type === 'link') {
    headerAction = (
      <Link
        testId={postfixTestId(props.testId, 'action-link')}
        wrapText={false}
        small
        disabled={props.headerAction.disabled ?? props.disabled}
        onPress={props.headerAction.onPress}
      >
        {props.headerAction.text}
      </Link>
    );
  }

  return (
    <div
      {...(setDraggableRefOnSectionContainer && { ...props.provided?.draggableProps })}
      {...(setDraggableRefOnSectionContainer && { ...props.provided?.dragHandleProps })}
      className={cx(
        cn.section,
        !props.isDragImage && isDraggable && cn.draggableItem,
        setDraggableRefOnSectionContainer && props.isDragImage && cn.dragImg
      )}
      data-testid={props.testId}
      style={{
        ...spacingTokens,
        ...(setDraggableRefOnSectionContainer &&
          (props.provided?.draggableProps.style as CSSProperties)),
        ...(setDraggableRefOnSectionContainer && !props.isDragImage && { transform: 'none' }),
      }}
      {...(setDraggableRefOnSectionContainer && { ref: props.provided?.innerRef })}
      tabIndex={undefined}
    >
      <div
        {...(!setDraggableRefOnSectionContainer && { ...props.provided?.draggableProps })}
        {...(!setDraggableRefOnSectionContainer && { ...props.provided?.dragHandleProps })}
        className={cx(
          cn.sectionHeader,
          props.isDragClone && cn.cloneImg,
          !setDraggableRefOnSectionContainer && props.isDragImage && cn.dragImg
        )}
        style={{
          ...(!setDraggableRefOnSectionContainer &&
            (props.provided?.draggableProps.style as CSSProperties)),
          ...(!setDraggableRefOnSectionContainer && !props.isDragImage && { transform: 'none' }),
        }}
        {...(!setDraggableRefOnSectionContainer && { ref: props.provided?.innerRef })}
        tabIndex={undefined}
      >
        <div className={cn.headingLayout}>
          <h5 className={cn.heading}>
            <div
              className={cx(cn.header, props.wrapHeaderText && cn.headerWrap)}
              ref={props.headerRef}
              style={headerTokenVars}
              onClick={props.onSelectHeader}
              onKeyDown={props.onKeyDown}
              // This is a button role to match UI5
              // FIXME: This role probably doesn't make sense for Accordions?
              role="button"
              aria-expanded={props.isOpen}
              aria-disabled={props.disabled}
              aria-describedby={props.required ? `${windowShadeSectionId}-required` : undefined}
              tabIndex={0}
              data-testid={postfixTestId(props.testId, 'header')}
            >
              <Icon
                testId={postfixTestId(props.testId, 'icon')}
                className={cx(
                  cn.headerIcon,
                  iconTransitionProps?.className,
                  isOpen ? cn.headerIconOpen : cn.headerIconClosed
                )}
                style={{ ...iconTransitionProps?.style }}
                icon={ArrowCollapsed}
                tokens={() => {
                  return getIconTokens(dt, props.variant);
                }}
              />
              {props.showHeadingIcon && props.headingIcon && (
                <Icon
                  testId={postfixTestId(props.testId, 'optionalIcon')}
                  className={cn.headerOptionalIcon}
                  icon={props.headingIcon.icon}
                  color={props.headingIcon.color}
                  tooltip={props.headingIcon.tooltip ?? ''}
                  ariaLabel={props.headingIcon.ariaLabel ?? ''}
                  tokens={() => {
                    return getIconTokens(dt, props.variant);
                  }}
                />
              )}
              <Text
                testId={postfixTestId(props.testId, 'heading')}
                wrapText={!!props.wrapHeaderText}
                className={cx(cn.headerText, props.headingClassName, props.disabled && cn.disabled)}
                style={props.headingStyle}
              >
                {props.header}
              </Text>
              {props.required && (
                <Fragment>
                  <span className={cn.requiredMark} aria-hidden>
                    {requiredSymbol}
                  </span>
                  <InvisibleText id={`${windowShadeSectionId}-required`}>
                    {localizer('header.required.aria')}
                  </InvisibleText>
                </Fragment>
              )}
            </div>
          </h5>
        </div>
        {headerAction && <div className={cn.headerAction}>{headerAction}</div>}
      </div>
      {/* FIXME: sometimes this has a role="region", not sure what the rules are. */}

      {!props.isDragImage && (
        <section
          className={cx(cn.content, sectionTransitionProps?.className)}
          style={{
            display: isOpen ? undefined : 'none',
            height: isOpen ? 'auto' : 0,
            maxHeight: props.maxHeight,
            overflowY: props.maxHeight ? 'auto' : 'hidden',
            ...sectionTransitionProps?.style,
          }}
          onTransitionEnd={(event) => {
            if (event.target === event.currentTarget) {
              setTransition({ isOpen: props.isOpen });
            }
          }}
          ref={contentSection}
          data-testid={postfixTestId(props.testId, 'content')}
        >
          <div className={cn.sectionContainer}>{props.children}</div>
        </section>
      )}
    </div>
  );
};

const getIconTokens = (dt: DesignTokens, variant: WindowShadeVariant | undefined) => {
  if (variant === 'primary-light') {
    switch (dt.themeVariant) {
      case 'high-contrast':
      case 'dark':
        return { fg: dt.c.colorTextDefault };
      default:
        return { fg: dt.c.colorTextInverse };
    }
  }
  return {};
};

const getSpacingTokens = (dt: DesignTokens) => ({
  'spacing-m': dt.spacing.core.m,
  'spacing-xxs': dt.spacing.core.xxs,
  'spacing-s': dt.spacing.core.s,
  'spacing-xs': dt.spacing.core.xs,

  'hoverState-color':
    dt.themeVariant === 'high-contrast' || dt.themeVariant === 'dark'
      ? dt.c.colorUiMedium
      : dt.backgroundsDepth.standard.bg,

  'medium-sh': dt.backgroundsDepth.medium.sh,
  'trans-dur': dt.animation.duration.exit.l,
  'anim-dur': dt.animation.duration.enter,
  'dragged-opacity': 0.3,
  'indicator-line-bg': dt.c.colorPrimary,
  'indicator-loop': dt.c.colorTextInverse,
});
